import { useEffect, useRef, useState } from "react";
import { ILiveStream, LivestreamLayoutCommonProps } from "../../../types";
import * as Styled from "./Layout5.styled";
import { ArrowUp, ArrowDown } from "react-bootstrap-icons";
import { BigCard } from "../../event-card/big-card";
import { SmallCard } from "../../event-card/small-card";
import { SeeCard } from "../../media-card/see-card";
import LayoutModal from "../../event-card/FullScreenModal";
import { LayoutHeader } from "../../layouts/LayoutHeader";
import { startVerticalScroll, stopVerticalScroll } from "../../../libs/utils";
import { filterEventsByType } from "../../../libs/utils/eventFilter";
import { EmptyLiveStreams } from "../../empty-state";

export const Layout5: React.FC<LivestreamLayoutCommonProps> = ({
    events,
    sessionToken,
    isSignedIn,
    activePlans,
    hasFullAccess,
    freeTrialPlanIds,
    onWatchClick,
}) => {
    const rightScrollRef = useRef<HTMLDivElement>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [mainEvent, setMainEvent] = useState<ILiveStream | undefined>(undefined);
    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
    const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [selectedOption, setSelectedOption] = useState<any>('All');

    useEffect(() => {
        if (events.length && !mainEvent) {
            const firstEvent = events[0];
            setMainEvent(firstEvent);
            setSelectedCardId(firstEvent.id);
        }
    }, [events, mainEvent]);

    const handleSeeAllClick = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const onCardClick = (item: ILiveStream) => {
        setMainEvent(item);
        setSelectedCardId(item.id);
    };

    const filteredEvents = filterEventsByType(events, selectedOption);

    if (!events.length) {
        return <EmptyLiveStreams />;
    }

    if (!mainEvent) return null;

    const remainingEvents = filteredEvents;

    return (
        <>
            <LayoutHeader
                title={'Live Streams'}
                itemCount={filteredEvents?.length}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                onSeeAll={handleSeeAllClick}
                padding="20px 10px 10px 0px"
                marginTop="0px"
                showToggle={true}
                isLivestream={true}
            />

            {
                filteredEvents.length === 0 ? (
                    <EmptyLiveStreams showHeader={false} isFiltered={selectedOption !== 'All'} />
                ) :
                    <Styled.Container>
                        <Styled.BigCard>
                            <BigCard
                                event={mainEvent}
                                isSignedIn={isSignedIn}
                                hasEligiblePlan={
                                    isSignedIn &&
                                    (!!mainEvent.planDetails?.length
                                        ? !!activePlans.filter((plan) =>
                                            mainEvent.planDetails?.some(
                                                (pd) => pd?.id === plan.id
                                            )
                                        ).length ||
                                        freeTrialPlanIds.includes(
                                            mainEvent.planDetails[0]?.id
                                        ) ||
                                        hasFullAccess
                                        : true)
                                }
                                onWatchClick={onWatchClick}
                            />
                        </Styled.BigCard>

                        <Styled.SidebarCard>
                            <Styled.SidebarCardContent>
                                <Styled.ScrollableList
                                    scrollable={remainingEvents.length > 4}
                                    ref={rightScrollRef}
                                >
                                    {remainingEvents.slice(0, 6).map((item) => (
                                        <Styled.SidebarCardWrapper key={item.id}>
                                            <SmallCard
                                                event={item}
                                                cardClick={() => onCardClick(item)}
                                                isSelected={item.id === selectedCardId}
                                                isSignedIn={isSignedIn}
                                                hasEligiblePlan={
                                                    isSignedIn &&
                                                    (!!item.planDetails?.length
                                                        ? !!activePlans.filter((plan) =>
                                                            item.planDetails?.some(
                                                                (pd) =>
                                                                    pd?.id === plan.id
                                                            )
                                                        ).length ||
                                                        freeTrialPlanIds.includes(
                                                            item.planDetails[0]?.id
                                                        ) ||
                                                        hasFullAccess
                                                        : true)
                                                }
                                                onWatchClick={onWatchClick}
                                                bodyWidth="280px"
                                            />
                                        </Styled.SidebarCardWrapper>
                                    ))}

                                    {/* SeeCard at end */}
                                    {remainingEvents.length > 6 && (
                                        <div onClick={handleSeeAllClick}>
                                            <SeeCard />
                                        </div>
                                    )}
                                </Styled.ScrollableList>
                            </Styled.SidebarCardContent>
                        </Styled.SidebarCard>

                        {remainingEvents.length > 4 && (
                            <Styled.ScrollButtonWrapper>
                                <Styled.ScrollButton
                                    onMouseDown={() =>
                                        startVerticalScroll(
                                            rightScrollRef,
                                            "up",
                                            scrollIntervalRef
                                        )
                                    }
                                    onMouseUp={() =>
                                        stopVerticalScroll(scrollIntervalRef)
                                    }
                                    onMouseLeave={() =>
                                        stopVerticalScroll(scrollIntervalRef)
                                    }
                                    title="Scroll Up"
                                >
                                    <ArrowUp size={30} />
                                </Styled.ScrollButton>

                                <Styled.ScrollButton
                                    onMouseDown={() =>
                                        startVerticalScroll(
                                            rightScrollRef,
                                            "down",
                                            scrollIntervalRef
                                        )
                                    }
                                    onMouseUp={() =>
                                        stopVerticalScroll(scrollIntervalRef)
                                    }
                                    onMouseLeave={() =>
                                        stopVerticalScroll(scrollIntervalRef)
                                    }
                                    title="Scroll Down"
                                >
                                    <ArrowDown size={30} />
                                </Styled.ScrollButton>
                            </Styled.ScrollButtonWrapper>
                        )}
                    </Styled.Container>}
            {/* Modal with Lazy Loaded Cards */}
            <LayoutModal
                show={isModalOpen}
                onHide={handleCloseModal}
                filteredLiveEvents={remainingEvents}
                isSignedIn={isSignedIn}
                activePlans={activePlans}
                hasFullAccess={hasFullAccess}
                freeTrialPlanIds={freeTrialPlanIds}
                onWatchClick={onWatchClick}
            />
        </>
    );
};
